import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Card,
  Chart,
  PivotTable,
  StatFigure,
} from '@employ-engage/common-components';
import {
  ChartResult,
  ChartSpec,
  summariseChartSpec,
} from '@employ-engage/data';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  DashboardTile as Tile,
  HEIGHT_PIXELS,
  SIZE_LABELS,
  SIZE_SHORT_LABELS,
  TileHeight,
  TileSize,
} from './tile-layout';
import { chartKindIsTable } from '../report-builder-page/chart-spec-builder';

export interface DashboardTileProps {
  tile: Tile;
  title: string;
  result?: ChartResult | null;
  loading?: boolean;
  error?: string | null;
  /**
   * The saved chart's spec, which carries everything about how it should be
   * drawn — kind, value format and measure.
   *
   * Passed whole rather than as separate props because the tile reading only
   * some of it is what made a pinned chart differ from the card it came from:
   * it took the kind and hardcoded the format.
   */
  spec?: ChartSpec | null;
  /** Set when the chart behind this tile has been removed. */
  missing?: boolean;
  /** A category clicked on this tile's chart, for cross-filtering. */
  onSelect?: (category: string) => void;
  /**
   * Why a dashboard-wide override did not reach this tile.
   *
   * Said out loud rather than passed over in silence: a tile that quietly
   * ignores the control above it looks identical to one that applied it and
   * happens to look the same, and that ambiguity is the bug the pinning work
   * existed to remove.
   */
  overrideNote?: string | null;
  /** "Created by …", in small grey type under the title. */
  byline?: string | null;
  onSizeChange: (size: TileSize) => void;
  onHeightChange: (height: TileHeight) => void;
  onRemove: () => void;
}

const SIZES: TileSize[] = ['sm', 'md', 'lg', 'full'];

export function DashboardTileCard({
  tile,
  title,
  result,
  loading,
  error,
  spec,
  missing,
  onSelect,
  overrideNote,
  byline,
  onSizeChange,
  onHeightChange,
  onRemove,
}: DashboardTileProps) {
  const { t } = useTranslation(['common']);

  /**
   * What the numbers are, what they are split by, and which window they cover.
   *
   * A tile is a picture and a title; nothing else on it said whether these
   * were counts or percentages. Two pins of the same question with different
   * settings looked identical — which they now can be, since a question may
   * be pinned more than once as long as the configuration differs.
   *
   * Read from the spec that was actually run, so a dashboard-wide override is
   * described as what is on screen rather than as what was pinned.
   */
  const summary = spec ? summariseChartSpec(spec) : null;

  const vizKind = spec?.visualization?.kind;
  const valueFormat = (spec?.visualization?.valueFormat ?? 'integer') as never;
  const isPercent = String(spec?.visualization?.valueFormat ?? '').startsWith(
    'percent'
  );
  // Which figure a 'stat' tile is showing, so its caption reads correctly.
  const statMeasure = (spec?.measure?.kind ?? 'topBox') as never;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tile.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Card>
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            {/* Drag by the handle only: the tile body is a chart with its own
                pointer handlers, and a whole-card drag target eats tooltips. */}
            <span
              {...listeners}
              {...attributes}
              className="cursor-grab touch-none text-gray-400 active:cursor-grabbing"
              aria-label={t('Drag to reorder')}
            >
              <i className="fa-solid fa-grip-dots-vertical" aria-hidden />
            </span>
            {/* A tile is narrow and a survey question is not, so the title
                is truncated. `title` gives the whole question back on hover
                and on focus, which is the only way to read it otherwise. */}
            <div className="min-w-0">
              <h3
                title={title}
                className="truncate text-sm font-semibold text-light-primaryText dark:text-dark-primaryText"
              >
                {title}
              </h3>
              {byline && (
                <p
                  title={byline}
                  className="truncate text-[11px] text-light-secondaryText dark:text-dark-secondaryText"
                >
                  {byline}
                </p>
              )}
            </div>
            {overrideNote && (
              <span
                title={overrideNote}
                aria-label={overrideNote}
                className="shrink-0 text-amber-500"
              >
                <i className="fa-solid fa-circle-exclamation" aria-hidden />
              </span>
            )}
          </div>

          <div className="shrink-0">
            {/* Anchored rather than absolutely positioned, which portals the
                panel to the document root. Two things clip it otherwise: Card
                is overflow-auto, and dnd-kit puts a transform on each tile,
                which makes that tile the containing block even for fixed
                positioning. */}
            <Menu as="div" className="relative inline-block text-left">
              <MenuButton
                aria-label={t('Tile options')}
                className="rounded px-2 py-1 text-xs text-light-secondaryText hover:bg-light-alternate focus:outline-none"
              >
                <i className="fa-solid fa-ellipsis" aria-hidden />
              </MenuButton>

              <MenuItems
                anchor={{ to: 'bottom end', gap: 6 }}
                className="z-50 w-60 rounded-xl bg-white p-3 shadow-lg ring-1 ring-black/5 focus:outline-none dark:bg-dark-secondaryBackground"
              >
                {/* Width and height are choices among a few fixed steps, so
                    they read as segmented controls rather than a list: the
                    options sit side by side the way the sizes do on the grid,
                    and the current one is filled in rather than merely bold,
                    which was easy to miss entirely. */}
                <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-light-secondaryText">
                  {t('Width')}
                </p>
                <div className="mb-3 grid grid-cols-4 gap-1">
                  {SIZES.map((size) => (
                    <button
                      key={size}
                      type="button"
                      onClick={() => onSizeChange(size)}
                      aria-pressed={tile.size === size}
                      title={t(SIZE_LABELS[size])}
                      className={classNames(
                        'rounded-lg border px-1 py-1.5 text-xs font-medium transition-colors',
                        tile.size === size
                          ? 'border-light-primary bg-light-primary text-white'
                          : 'border-gray-200 text-light-secondaryText hover:bg-light-alternate dark:border-gray-700'
                      )}
                    >
                      {t(SIZE_SHORT_LABELS[size])}
                    </button>
                  ))}
                </div>

                <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-light-secondaryText">
                  {t('Height')}
                </p>
                <div className="mb-3 grid grid-cols-2 gap-1">
                  {(['short', 'tall'] as TileHeight[]).map((height) => (
                    <button
                      key={height}
                      type="button"
                      onClick={() => onHeightChange(height)}
                      aria-pressed={tile.height === height}
                      className={classNames(
                        'rounded-lg border px-2 py-1.5 text-xs font-medium transition-colors',
                        tile.height === height
                          ? 'border-light-primary bg-light-primary text-white'
                          : 'border-gray-200 text-light-secondaryText hover:bg-light-alternate dark:border-gray-700'
                      )}
                    >
                      {height === 'short' ? t('Short') : t('Tall')}
                    </button>
                  ))}
                </div>

                <div className="my-2 border-t border-gray-200 dark:border-gray-700" />

                <MenuItem>
                  <button
                    type="button"
                    onClick={onRemove}
                    className="flex w-full items-center gap-2 whitespace-nowrap rounded-lg px-2 py-2 text-left text-sm text-red-600 data-[focus]:bg-red-50"
                  >
                    <i className="fa-solid fa-trash w-4 text-xs" aria-hidden />
                    {t('Remove from dashboard')}
                  </button>
                </MenuItem>
              </MenuItems>
            </Menu>
          </div>
        </div>

        {summary && !missing && (
          <p className="mt-1 truncate text-xs text-light-secondaryText dark:text-dark-secondaryText">
            {[
              t(summary.measure),
              summary.breakdown
                ? t('by {{name}}', { name: summary.breakdown })
                : null,
              summary.period.kind === 'label'
                ? t(summary.period.label)
                : `${t('dateShort', {
                    date: new Date(summary.period.startDate),
                  })} – ${t('dateShort', {
                    date: new Date(summary.period.endDate),
                  })}`,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        )}

        {/* A definite height, and deliberately not flex-1: flex-basis:0% wins
            over height on the main axis, so the box would collapse to nothing
            and ResponsiveContainer would render an empty chart. */}
        <div
          className="mt-3 overflow-auto"
          style={{ height: HEIGHT_PIXELS[tile.height] }}
        >
          {missing ? (
            // The chart was deleted by someone else. Say so plainly rather
            // than showing an empty plot that looks like missing data.
            <div className="flex h-full items-center justify-center p-4 text-center text-sm text-light-secondaryText">
              {t('This chart has been removed.')}
            </div>
          ) : result?.error ? (
            // This tile's spec failed while the others on the dashboard ran.
            // An empty plot here would read as "nobody answered", which is a
            // different thing entirely. The reason sits in the title rather
            // than on the tile: it is for whoever is diagnosing it, and it
            // comes from the server unedited.
            <div
              className="flex h-full items-center justify-center p-4 text-center text-sm text-light-secondaryText"
              title={result.error}
            >
              {t('This chart could not be run.')}
            </div>
          ) : vizKind === 'stat' ? (
            // A scored measure with no breakdown is one figure, not a chart.
            // Without this branch it fell through to a bar chart with no rows
            // and rendered "No responses in this range yet", where the card it
            // was pinned from showed a number.
            <StatFigure result={result} measure={statMeasure} />
          ) : chartKindIsTable(vizKind) ? (
            <PivotTable
              result={result}
              display={isPercent ? 'both' : 'count'}
              heatmap
            />
          ) : (
            <Chart
              result={result}
              onSelect={onSelect}
              type={(vizKind as never) ?? 'horizontalBar'}
              loading={loading}
              error={error}
              // Read from the spec rather than hardcoded: a chart pinned with
              // % on stored 'percent0' and was drawn as raw counts anyway.
              valueFormat={valueFormat}
            />
          )}
        </div>
      </Card>
    </div>
  );
}

export default DashboardTileCard;
